import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonService } from './services/common/common.service';
import { Location } from '@angular/common';
import { AuthenticationResult } from '@azure/msal-browser';
import { MonitorService } from './services/monitor/monitor.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './services/auth/auth.service';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { ExternalScriptLoaderService } from './services/script-loader/external-script-loader.service';

/**
 * Returns true when the app is running inside an iframe.
 * Replaces MSAL Browser v2 `BrowserUtils.isInIframe()` (broken typing under msal-browser v3).
 */
function isAppInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'car-heal-quote';
  isIframe = false;
  loginDisplay = false;

  /**
   * constructor
   * @param commonService
   */
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private readonly authService: AuthService,
    private readonly location: Location,
    private readonly monitorService: MonitorService,
    private readonly translateService: TranslateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly scriptLoader: ExternalScriptLoaderService,
    public commonService: CommonService
  ) {
    this.authService.handleAuthorizationConfig((result: AuthenticationResult): void => {
      this.monitorService.logEvent('Login Success', [ 'Login', 'addenda-quote' ],
        { 'userId': result.account?.localAccountId, 'email': result.account?.username, 'trackingId': result.correlationId });
    });

    this.commonService.locale$.subscribe(() => {
      this.cdRef.markForCheck();
    });
    // Body #custom-loader is toggled by show/hideLoading (above mat-dialog). Keep CD for notification flag.
    this.commonService.loadingChanged$.subscribe(() => {
      this.cdRef.markForCheck();
    });
    // The notification auto-hides from a timer, which does not trigger change detection on its own.
    this.commonService.notificationChanged$.subscribe(() => {
      this.cdRef.markForCheck();
    });
  }

  /**
   * ngAfterViewInit hook
   * @param fb FormBuilder 
   */
  ngAfterViewInit(): void {
    this.commonService.updatePage('/quote/case/0');
    this.monitorService.logEvent('ngAfterViewInit', [ 'AppComponent', 'addenda-quote' ]);
    this.deferAnalytics();
  }

  /**
   * Loads Google Analytics after first paint so it does not compete with bootstrap.
   */
  private deferAnalytics(): void {
    const schedule = (window as any).requestIdleCallback as ((cb: () => void) => void) | undefined;
    if (typeof schedule === 'function') {
      schedule(() => {
        void this.scriptLoader.loadGoogleAnalytics();
      });
      return;
    }

    setTimeout(() => {
      void this.scriptLoader.loadGoogleAnalytics();
    }, 0);
  }

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    const currentPath = this.location.path();
    this.monitorService.logEvent('ngOnInit', [ 'AppComponent', 'addenda-quote' ]);
    // Dont perform nav if in iframe or popup, other than for front-channel logout
    this.isIframe = isAppInIframe() && !window.opener && currentPath.indexOf('logout') < 0; // Remove this line to use Angular Universal

    const defaultLang = localStorage.getItem('defaultLanguage');
    if (defaultLang) {
      this.translateService.use(defaultLang);
    }
  }
}
